// src/hooks/useWeb3.js - UPDATED WITH BETTER EVENT HANDLING
import { useState, useEffect } from 'react';
import { createPublicClient, createWalletClient, http, custom } from 'viem';
import { baseSepolia } from 'viem/chains';
import { CONFIG } from '../config/constants';
import { detectWallet } from '../utils/formatters';

// ============ WEB3 HOOK ============
export const useWeb3 = () => {
  const [account, setAccount] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [publicClient, setPublicClient] = useState(null);
  const [walletClient, setWalletClient] = useState(null);
  const [chainId, setChainId] = useState(null);

  useEffect(() => {
    // Create public client
    const client = createPublicClient({
	    chain: baseSepolia,
	    transport: http(CONFIG.RPC_URL || "https://sepolia.base.org")
	    });
	    setPublicClient(client);

    // Check if already connected
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          const accounts = await window.ethereum.request({ 
            method: 'eth_accounts' 
          });
          
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            setWalletType(detectWallet());
            
            // Create wallet client if connected
            const wallet = createWalletClient({
              account: accounts[0],
              chain: baseSepolia,
              transport: custom(window.ethereum)
            });
            setWalletClient(wallet);
          }
        } catch (error) {
          console.error('Error checking connection:', error);
        }
      }
    };

    checkConnection();

    // Set up event listeners
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        console.log('Accounts changed:', accounts);
        if (accounts.length === 0) {
          // User disconnected
          setAccount(null);
          setWalletClient(null);
          setWalletType(null);
        } else {
          // User switched accounts
          setAccount(accounts[0]);
          setWalletType(detectWallet());
          
          // Update wallet client
          const wallet = createWalletClient({
            account: accounts[0],
            chain: baseSepolia,
            transport: custom(window.ethereum)
          });
          setWalletClient(wallet);
        }
      };

      const handleChainChanged = (chainId) => {
        console.log('Chain changed:', chainId);
        setChainId(chainId);
        // Reload page to ensure proper chain handling
        window.location.reload();
      };

      const handleDisconnect = () => {
        console.log('Wallet disconnected');
        setAccount(null);
        setWalletClient(null);
        setWalletType(null);
      };

      // Add event listeners
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('disconnect', handleDisconnect);

      // Cleanup function
      return () => {
        if (window.ethereum) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
          window.ethereum.removeListener('disconnect', handleDisconnect);
        }
      };
    }
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask or another Web3 wallet!');
      return;
    }

    try {
      // Request accounts
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      // Check and switch to Base Sepolia if needed
      const currentChainId = await window.ethereum.request({ 
        method: 'eth_chainId' 
      });
      
      const baseSepoliaChainId = '0x14a34'; // Base Sepolia
      
      if (currentChainId !== baseSepoliaChainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: baseSepoliaChainId }],
          });
        } catch (switchError) {
          // This error code indicates that the chain has not been added to MetaMask
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: baseSepoliaChainId,
                  chainName: 'Base Sepolia',
                  nativeCurrency: { 
                    name: 'ETH', 
                    symbol: 'ETH', 
                    decimals: 18 
                  },
                  rpcUrls: ['https://sepolia.base.org'],
                  blockExplorerUrls: ['https://sepolia.basescan.org']
                }]
              });
            } catch (addError) {
              console.error('Error adding chain:', addError);
              alert('Failed to add Base Sepolia network. Please add it manually.');
            }
          } else {
            console.error('Error switching chain:', switchError);
          }
        }
      }

      // Create wallet client
      const wallet = createWalletClient({
        account: accounts[0],
        chain: baseSepolia,
        transport: custom(window.ethereum)
      });

      // Update state
      setAccount(accounts[0]);
      setWalletClient(wallet);
      setWalletType(detectWallet());
      
      console.log('Connected successfully:', accounts[0]);
      
    } catch (error) {
      console.error('Connection error:', error);
      if (error.code === 4001) {
        alert('Connection rejected by user.');
      } else {
        alert('Connection failed: ' + error.message);
      }
    }
  };

  const disconnect = () => {
    setAccount(null);
    setWalletClient(null);
    setWalletType(null);
    setChainId(null);
    console.log('Disconnected');
  };

  return { 
    account, 
    walletType, 
    publicClient, 
    walletClient, 
    connectWallet, 
    disconnect, 
    isConnected: !!account,
    chainId
  };
};